<template>
  <div class="exercise-tracker">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to track your workouts and monitor your exercise progress.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row g-4">
      <!-- Exercise List -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-lightning-charge me-2"></i>My Workouts</h5>
            <button class="btn btn-primary btn-sm" @click="showAddModal = true">
              <i class="bi bi-plus me-1"></i>Add Workout
            </button>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-muted mt-2">Loading workouts...</p>
            </div>
            
            <div v-else-if="pendingExercises.length === 0" class="text-center py-4">
              <i class="bi bi-dumbbell text-muted" style="font-size: 3rem"></i>
              <p class="text-muted mt-2">No pending workouts. Start your fitness journey!</p>
            </div>

            <div v-else>
              <div
                v-for="exercise in pendingExercises"
                :key="exercise.id"
                class="exercise-item d-flex justify-content-between align-items-center p-3 mb-2 border rounded"
              >
                <div class="exercise-info">
                  <h6 class="mb-1">{{ exercise.name }}</h6>
                  <small class="text-muted">
                    {{ exercise.duration }} min • {{ exercise.calories }} cal • {{ exercise.date }}
                  </small>
                </div>
                <div class="exercise-actions">
                  <button
                    v-if="!exercise.completed"
                    class="btn btn-sm btn-success me-2"
                    @click="completeExercise(exercise.id)"
                    title="Mark as completed"
                  >
                    <i class="bi bi-check-circle"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-primary me-2"
                    @click="editExercise(exercise)"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="deleteExercise(exercise.id)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Completed Workouts -->
        <div v-if="completedExercises.length > 0" class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-check-circle-fill me-2 text-success"></i>Completed Workouts</h5>
          </div>
          <div class="card-body">
            <div
              v-for="exercise in completedExercises"
              :key="exercise.id"
              class="exercise-item completed-exercise d-flex justify-content-between align-items-center p-3 mb-2 border rounded"
            >
              <div class="exercise-info">
                <h6 class="mb-1 text-success">
                  <i class="bi bi-check-circle-fill me-2"></i>{{ exercise.name }}
                </h6>
                <small class="text-muted">
                  {{ exercise.duration }} min • {{ exercise.calories }} cal • {{ exercise.date }}
                  <span class="badge bg-success ms-2">Completed {{ formatCompletedDate(exercise.completedAt) }}</span>
                </small>
              </div>
              <div class="exercise-actions">
                <button
                  class="btn btn-sm btn-outline-warning me-2"
                  @click="uncompleteExercise(exercise.id)"
                  title="Mark as incomplete"
                >
                  <i class="bi bi-arrow-counterclockwise"></i>
                </button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="deleteExercise(exercise.id)"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="col-lg-4">
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-zap me-2"></i>Quick Workouts</h6>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <button
                v-for="workout in quickWorkouts"
                :key="workout.name"
                class="btn btn-outline-primary btn-sm text-start"
                @click="startQuickWorkout(workout)"
              >
                <i :class="workout.icon + ' me-2'"></i>{{ workout.name }}
                <small class="text-muted d-block">{{ workout.duration }} min</small>
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-stopwatch me-2"></i>Workout Timer</h6>
          </div>
          <div class="card-body text-center">
            <div class="timer-display mb-3">
              <span class="timer-time">{{ formatTime(timerSeconds) }}</span>
            </div>
            <div class="d-flex gap-2 justify-content-center">
              <button class="btn btn-success btn-sm" @click="startTimer" :disabled="timerActive">
                <i class="bi bi-play"></i>
              </button>
              <button class="btn btn-warning btn-sm" @click="pauseTimer" :disabled="!timerActive">
                <i class="bi bi-pause"></i>
              </button>
              <button class="btn btn-danger btn-sm" @click="resetTimer">
                <i class="bi bi-stop"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Exercise Modal -->
    <div
      v-if="showAddModal"
      class="modal d-block"
      style="background: rgba(0, 0, 0, 0.5)"
      @click.self="showAddModal = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingExercise ? 'Edit' : 'Add' }} Workout</h5>
            <button type="button" class="btn-close" @click="showAddModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveExercise">
              <div class="mb-3">
                <label class="form-label">Exercise Name</label>
                <input type="text" class="form-control" v-model="exerciseForm.name" required />
              </div>
              <div class="mb-3">
                <label class="form-label">Duration (minutes)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="exerciseForm.duration"
                  min="1"
                  required
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Calories Burned</label>
                <input type="number" class="form-control" v-model="exerciseForm.calories" min="0" />
              </div>
              <div class="mb-3">
                <label class="form-label">Date</label>
                <input type="date" class="form-control" v-model="exerciseForm.date" required />
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAddModal = false">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="saveExercise">
              {{ editingExercise ? 'Update' : 'Add' }} Workout
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { ExerciseService } from '@/services/exerciseService'

export default {
  name: 'ExerciseTracker',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      exercises: [],
      loading: false,
      exerciseService: null,
      showAddModal: false,
      editingExercise: null,
      exerciseForm: {
        name: '',
        duration: '',
        calories: '',
        date: new Date().toISOString().split('T')[0],
      },
      timerSeconds: 0,
      timerActive: false,
      timerInterval: null,
      quickWorkouts: [
        { name: 'Morning Yoga', duration: 15, icon: 'bi bi-moon' },
        { name: 'HIIT Cardio', duration: 20, icon: 'bi bi-lightning' },
        { name: 'Strength Training', duration: 30, icon: 'bi bi-dumbbell' },
        { name: 'Evening Walk', duration: 25, icon: 'bi bi-sunset' },
      ],
    }
  },

  watch: {
    user: {
      handler(newUser) {
        if (newUser && newUser.uid) {
          this.exerciseService = new ExerciseService(newUser.uid)
          this.loadExercises()
        }
      },
      immediate: true
    }
  },

  computed: {
    completedExercises() {
      return this.exercises.filter(exercise => exercise.completed)
    },
    
    pendingExercises() {
      return this.exercises.filter(exercise => !exercise.completed)
    },

    weeklyStats() {
      const now = new Date()
      const weekStart = new Date(now.setDate(now.getDate() - now.getDay()))
      weekStart.setHours(0, 0, 0, 0)
      
      const thisWeekCompleted = this.completedExercises.filter(exercise => {
        const completedDate = new Date(exercise.completedAt)
        return completedDate >= weekStart
      })

      return {
        workoutsThisWeek: thisWeekCompleted.length,
        totalMinutes: thisWeekCompleted.reduce((sum, ex) => sum + parseInt(ex.duration || 0), 0),
        caloriesBurned: thisWeekCompleted.reduce((sum, ex) => sum + parseInt(ex.calories || 0), 0)
      }
    }
  },

  async mounted() {
    this.initAuth()
    
    // Migrate localStorage data if exists and user is logged in
    if (this.isAuthenticated && localStorage.getItem('exercises')) {
      await this.migrateLocalStorageData()
    }
  },

  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },

  methods: {
    async loadExercises() {
      if (!this.exerciseService) return
      
      this.loading = true
      try {
        const result = await this.exerciseService.getExercises()
        if (result.success) {
          this.exercises = result.exercises
        } else {
          console.error('Failed to load exercises:', result.error)
        }
      } catch (error) {
        console.error('Failed to load exercises:', error)
      } finally {
        this.loading = false
      }
    },

    async saveExercise() {
      if (!this.exerciseService) return

      this.loading = true
      try {
        if (this.editingExercise) {
          // Update existing exercise
          const result = await this.exerciseService.updateExercise(
            this.editingExercise.id, 
            this.exerciseForm
          )
          if (result.success) {
            await this.loadExercises()
          } else {
            console.error('Failed to update exercise:', result.error)
          }
        } else {
          // Add new exercise
          const result = await this.exerciseService.addExercise(this.exerciseForm)
          if (result.success) {
            await this.loadExercises()
          } else {
            console.error('Failed to add exercise:', result.error)
          }
        }
        
        this.resetForm()
        this.showAddModal = false
      } catch (error) {
        console.error('Failed to save exercise:', error)
      } finally {
        this.loading = false
      }
    },

    editExercise(exercise) {
      this.editingExercise = exercise
      this.exerciseForm = { ...exercise }
      this.showAddModal = true
    },

    async deleteExercise(id) {
      if (!this.exerciseService) return
      
      if (confirm('Are you sure you want to delete this workout?')) {
        try {
          const result = await this.exerciseService.deleteExercise(id)
          if (result.success) {
            await this.loadExercises()
          } else {
            console.error('Failed to delete exercise:', result.error)
          }
        } catch (error) {
          console.error('Failed to delete exercise:', error)
        }
      }
    },

    async completeExercise(id) {
      if (!this.exerciseService) return
      
      try {
        const result = await this.exerciseService.completeExercise(id)
        if (result.success) {
          await this.loadExercises()
        } else {
          console.error('Failed to complete exercise:', result.error)
        }
      } catch (error) {
        console.error('Failed to complete exercise:', error)
      }
    },

    async uncompleteExercise(id) {
      if (!this.exerciseService) return
      
      try {
        const result = await this.exerciseService.uncompleteExercise(id)
        if (result.success) {
          await this.loadExercises()
        } else {
          console.error('Failed to uncomplete exercise:', result.error)
        }
      } catch (error) {
        console.error('Failed to uncomplete exercise:', error)
      }
    },

    async migrateLocalStorageData() {
      try {
        const localData = JSON.parse(localStorage.getItem('exercises') || '[]')
        if (localData.length > 0 && this.exerciseService) {
          for (const exercise of localData) {
            // Remove local ID, let Firebase generate new one
            const { id, ...exerciseData } = exercise
            await this.exerciseService.addExercise(exerciseData)
          }
          // Clear localStorage data
          localStorage.removeItem('exercises')
          // Reload data
          await this.loadExercises()
        }
      } catch (error) {
        console.error('Migration failed:', error)
      }
    },

    resetForm() {
      this.exerciseForm = {
        name: '',
        duration: '',
        calories: '',
        date: new Date().toISOString().split('T')[0],
      }
      this.editingExercise = null
    },

    formatCompletedDate(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) {
        return 'today'
      } else if (diffDays === 1) {
        return 'yesterday'
      } else if (diffDays < 7) {
        return `${diffDays} days ago`
      } else {
        return date.toLocaleDateString()
      }
    },

    startQuickWorkout(workout) {
      this.exerciseForm.name = workout.name
      this.exerciseForm.duration = workout.duration
      this.exerciseForm.calories = workout.duration * 5 // Rough estimate
      this.showAddModal = true
    },

    startTimer() {
      this.timerActive = true
      this.timerInterval = setInterval(() => {
        this.timerSeconds++
      }, 1000)
    },

    pauseTimer() {
      this.timerActive = false
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }
    },

    resetTimer() {
      this.timerActive = false
      this.timerSeconds = 0
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
.exercise-item {
  transition: all 0.3s ease;
}

.exercise-item:hover {
  background-color: #f8f9fa;
  transform: translateX(5px);
}

.completed-exercise {
  background-color: rgba(40, 167, 69, 0.05);
  border-color: #28a745 !important;
}

.completed-exercise:hover {
  background-color: rgba(40, 167, 69, 0.1);
}

.timer-display {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1rem;
}

.timer-time {
  font-size: 2rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  color: #495057;
}

.modal {
  z-index: 1050;
}

.btn-outline-primary:hover {
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .exercise-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .exercise-actions {
    margin-top: 0.5rem;
    align-self: flex-end;
  }

  .timer-time {
    font-size: 1.5rem;
  }
}
</style>
